import { Observable } from 'rxjs';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import * as HrModels from '../hr.models';
import { Constructor, HrApiClient } from '../shared/hr-api-client';

export function withHrPayrollApi<TBase extends Constructor<HrApiClient>>(Base: TBase) {
  return class extends Base {
    payrollDashboard(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrPayrollDashboard>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPayrollDashboard>>(`${this.baseUrl}/payroll/dashboard`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    payElements(owningCompanyId?: number, active?: boolean): Observable<APIResponseWrapper<HrModels.HrPayElement[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPayElement[]>>(`${this.baseUrl}/payroll/pay-elements`, {
          params: this.params({ owningCompanyId, active })
        });
      }
    
    savePayElement(request: HrModels.HrPayElement): Observable<APIResponseWrapper<HrModels.HrPayElement>> {
        return request.payElementId
          ? this.http.put<APIResponseWrapper<HrModels.HrPayElement>>(`${this.baseUrl}/payroll/pay-elements/${request.payElementId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrPayElement>>(`${this.baseUrl}/payroll/pay-elements`, request);
      }
    
    compensationPackages(owningCompanyId?: number, employeeId?: number, status?: string): Observable<APIResponseWrapper<HrModels.HrCompensationPackage[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrCompensationPackage[]>>(`${this.baseUrl}/payroll/compensation-packages`, {
          params: this.params({ owningCompanyId, employeeId, status })
        });
      }
    
    saveCompensationPackage(request: HrModels.HrCompensationPackage): Observable<APIResponseWrapper<HrModels.HrCompensationPackage>> {
        return request.compensationPackageId
          ? this.http.put<APIResponseWrapper<HrModels.HrCompensationPackage>>(`${this.baseUrl}/payroll/compensation-packages/${request.compensationPackageId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrCompensationPackage>>(`${this.baseUrl}/payroll/compensation-packages`, request);
      }
    
    approveCompensationPackage(compensationPackageId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrCompensationPackage>> {
        return this.http.post<APIResponseWrapper<HrModels.HrCompensationPackage>>(`${this.baseUrl}/payroll/compensation-packages/${compensationPackageId}/approve`, null, {
          params: this.params({ owningCompanyId })
        });
      }
    
    saveCompensationPackageElement(compensationPackageId: number, request: HrModels.HrCompensationPackageElement): Observable<APIResponseWrapper<HrModels.HrCompensationPackageElement>> {
        return request.compensationPackageElementId
          ? this.http.put<APIResponseWrapper<HrModels.HrCompensationPackageElement>>(`${this.baseUrl}/payroll/compensation-packages/${compensationPackageId}/elements/${request.compensationPackageElementId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrCompensationPackageElement>>(`${this.baseUrl}/payroll/compensation-packages/${compensationPackageId}/elements`, request);
      }
    
    payrollPeriods(owningCompanyId?: number, status?: string, limit?: number): Observable<APIResponseWrapper<HrModels.HrPayrollPeriod[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPayrollPeriod[]>>(`${this.baseUrl}/payroll/periods`, {
          params: this.params({ owningCompanyId, status, limit })
        });
      }
    
    savePayrollPeriod(request: HrModels.HrPayrollPeriod): Observable<APIResponseWrapper<HrModels.HrPayrollPeriod>> {
        return request.payrollPeriodId
          ? this.http.put<APIResponseWrapper<HrModels.HrPayrollPeriod>>(`${this.baseUrl}/payroll/periods/${request.payrollPeriodId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrPayrollPeriod>>(`${this.baseUrl}/payroll/periods`, request);
      }
    
    lockPayrollPeriod(payrollPeriodId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrPayrollPeriod>> {
        return this.http.post<APIResponseWrapper<HrModels.HrPayrollPeriod>>(`${this.baseUrl}/payroll/periods/${payrollPeriodId}/lock`, null, {
          params: this.params({ owningCompanyId })
        });
      }
    
    payrollRuns(owningCompanyId?: number, payrollPeriodId?: number, limit?: number): Observable<APIResponseWrapper<HrModels.HrPayrollRun[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPayrollRun[]>>(`${this.baseUrl}/payroll/runs`, {
          params: this.params({ owningCompanyId, payrollPeriodId, limit })
        });
      }
    
    payrollRun(payrollRunId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrPayrollRun>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPayrollRun>>(`${this.baseUrl}/payroll/runs/${payrollRunId}`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    calculatePayrollRun(request: HrModels.HrPayrollRunCalculationRequest): Observable<APIResponseWrapper<HrModels.HrPayrollRun>> {
        return this.http.post<APIResponseWrapper<HrModels.HrPayrollRun>>(`${this.baseUrl}/payroll/runs/calculate`, request);
      }
    
    approvePayrollRun(payrollRunId: number, owningCompanyId?: number, reason?: string): Observable<APIResponseWrapper<HrModels.HrPayrollRun>> {
        return this.http.post<APIResponseWrapper<HrModels.HrPayrollRun>>(`${this.baseUrl}/payroll/runs/${payrollRunId}/approve`, { owningCompanyId, reason });
      }
    
    payrollRunLines(payrollRunId: number, owningCompanyId?: number, includeElements = true): Observable<APIResponseWrapper<HrModels.HrPayrollRunLine[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPayrollRunLine[]>>(`${this.baseUrl}/payroll/runs/${payrollRunId}/lines`, {
          params: this.params({ owningCompanyId, includeElements })
        });
      }
    
    payrollExceptions(owningCompanyId?: number, payrollRunId?: number, status?: string, limit?: number): Observable<APIResponseWrapper<HrModels.HrPayrollException[]>> {
        const url = payrollRunId ? `${this.baseUrl}/payroll/runs/${payrollRunId}/exceptions` : `${this.baseUrl}/payroll/exceptions`;
        return this.http.get<APIResponseWrapper<HrModels.HrPayrollException[]>>(url, {
          params: this.params({ owningCompanyId, status, limit })
        });
      }
    
    payrollInputs(owningCompanyId?: number, employeeId?: number, status?: string, limit?: number): Observable<APIResponseWrapper<HrModels.HrPayrollInput[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPayrollInput[]>>(`${this.baseUrl}/payroll/inputs`, {
          params: this.params({ owningCompanyId, employeeId, status, limit })
        });
      }
    
    savePayrollInput(request: HrModels.HrPayrollInput): Observable<APIResponseWrapper<HrModels.HrPayrollInput>> {
        return request.payrollInputId
          ? this.http.put<APIResponseWrapper<HrModels.HrPayrollInput>>(`${this.baseUrl}/payroll/inputs/${request.payrollInputId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrPayrollInput>>(`${this.baseUrl}/payroll/inputs`, request);
      }
    
    generatePayrollPayslips(payrollRunId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrPayslip[]>> {
        return this.http.post<APIResponseWrapper<HrModels.HrPayslip[]>>(`${this.baseUrl}/payroll/runs/${payrollRunId}/payslips/generate`, null, {
          params: this.params({ owningCompanyId })
        });
      }
    
    payrollPayslips(owningCompanyId?: number, payrollRunId?: number, employeeId?: number): Observable<APIResponseWrapper<HrModels.HrPayslip[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPayslip[]>>(`${this.baseUrl}/payroll/payslips`, {
          params: this.params({ owningCompanyId, payrollRunId, employeeId })
        });
      }
    
    payrollAccountingMappings(owningCompanyId?: number, active?: boolean): Observable<APIResponseWrapper<HrModels.HrPayrollAccountingMapping[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPayrollAccountingMapping[]>>(`${this.baseUrl}/payroll/accounting/mappings`, {
          params: this.params({ owningCompanyId, active })
        });
      }
    
    savePayrollAccountingMapping(request: HrModels.HrPayrollAccountingMapping): Observable<APIResponseWrapper<HrModels.HrPayrollAccountingMapping>> {
        return request.payrollAccountingMappingId
          ? this.http.put<APIResponseWrapper<HrModels.HrPayrollAccountingMapping>>(`${this.baseUrl}/payroll/accounting/mappings/${request.payrollAccountingMappingId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrPayrollAccountingMapping>>(`${this.baseUrl}/payroll/accounting/mappings`, request);
      }
    
    payrollPostingPreview(payrollRunId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrPayrollAccountingPreview>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPayrollAccountingPreview>>(`${this.baseUrl}/payroll/runs/${payrollRunId}/posting-preview`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    postPayrollAccounting(payrollRunId: number, owningCompanyId?: number, reason?: string): Observable<APIResponseWrapper<HrModels.HrPayrollAccountingBatch>> {
        return this.http.post<APIResponseWrapper<HrModels.HrPayrollAccountingBatch>>(`${this.baseUrl}/payroll/runs/${payrollRunId}/post`, { owningCompanyId, reason });
      }
    
    createPayrollPaymentProposal(payrollRunId: number, owningCompanyId?: number, reason?: string): Observable<APIResponseWrapper<HrModels.HrPayrollPaymentBatch>> {
        return this.http.post<APIResponseWrapper<HrModels.HrPayrollPaymentBatch>>(`${this.baseUrl}/payroll/runs/${payrollRunId}/payment-proposal`, { owningCompanyId, reason });
      }
    
    reversePayrollAccounting(payrollRunId: number, owningCompanyId?: number, reason?: string): Observable<APIResponseWrapper<HrModels.HrPayrollAccountingBatch>> {
        return this.http.post<APIResponseWrapper<HrModels.HrPayrollAccountingBatch>>(`${this.baseUrl}/payroll/runs/${payrollRunId}/reverse-accounting`, { owningCompanyId, reason });
      }
    
    payrollAccountingBatches(owningCompanyId?: number, payrollRunId?: number, limit?: number): Observable<APIResponseWrapper<HrModels.HrPayrollAccountingBatch[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPayrollAccountingBatch[]>>(`${this.baseUrl}/payroll/accounting/batches`, {
          params: this.params({ owningCompanyId, payrollRunId, limit })
        });
      }
    
    payrollPaymentBatches(owningCompanyId?: number, payrollRunId?: number, limit?: number): Observable<APIResponseWrapper<HrModels.HrPayrollPaymentBatch[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPayrollPaymentBatch[]>>(`${this.baseUrl}/payroll/payment/batches`, {
          params: this.params({ owningCompanyId, payrollRunId, limit })
        });
      }
  };
}

